import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const getName = (lead) => {
  if (!lead) return "Unknown Lead";

  return (
    lead.name ||
    [lead.firstName, lead.lastName].filter(Boolean).join(" ") ||
    "Unknown Lead"
  );
};

const getInitials = (name) => {
  if (!name) return "L";

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

const formatDate = (value, includeTime = false) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  });
};

const getStatusClass = (status) => {
  switch (status) {
    case "New":
      return "bg-primary-subtle text-primary";
    case "Contacted":
      return "bg-info-subtle text-info";
    case "Follow-up":
      return "bg-warning-subtle text-warning";
    case "Qualified":
      return "bg-success-subtle text-success";
    case "Counselling":
      return "bg-secondary-subtle text-secondary";
    case "Application":
      return "bg-primary-subtle text-primary";
    case "Admitted":
      return "bg-success-subtle text-success";
    case "Lost":
      return "bg-danger-subtle text-danger";
    default:
      return "bg-light text-muted";
  }
};

const getPriorityClass = (priority) => {
  switch (priority) {
    case "High":
      return "bg-danger-subtle text-danger";
    case "Medium":
      return "bg-warning-subtle text-warning";
    case "Low":
      return "bg-success-subtle text-success";
    default:
      return "bg-light text-muted";
  }
};

const DetailItem = ({ label, value, children }) => (
  <div className="mb-3">
    <div className="text-muted fs-12 mb-1">{label}</div>
    <div className="fw-medium">{children || value || "—"}</div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h6 className="text-uppercase text-muted fs-12 fw-semibold mb-3">
    {children}
  </h6>
);

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activities, setActivities] = useState([]);

  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchLead = useCallback(async () => {
    if (!id) return;

    setLoading(true);

    try {
      const response = await fetch(`/leads/${id}`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.message || "Unable to load lead details."
        );
      }

      const data = result?.data || {};

      setLead(data.lead || null);
      setProfile(data.profile || null);
      setActivities(data.activities || []);
    } catch (error) {
      console.error("Lead details error:", error);

      toast.error(
        error?.message || "Unable to load lead details."
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const leadName = useMemo(
    () => getName(lead),
    [lead]
  );

  const phone = lead?.mobile || lead?.phone || "";
  const whatsapp = lead?.whatsapp || phone;
  const email = lead?.email || "";

  const handleDelete = async () => {
    if (!lead?._id) return;

    setDeleting(true);

    try {
      const response = await fetch(
        `/leads/${lead._id}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.message || "Unable to delete lead."
        );
      }

      toast.success("Lead deleted successfully.");

      setDeleteModal(false);

      setTimeout(() => {
        navigate("/leads", { replace: true });
      }, 500);
    } catch (error) {
      console.error("Delete lead error:", error);

      toast.error(
        error?.message || "Unable to delete lead."
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <BreadCrumb
              title="Lead Details"
              pageTitle="Leads"
            />

            <Card>
              <CardBody className="text-center py-5">
                <Spinner
                  color="primary"
                  className="mb-3"
                />

                <h5 className="mb-1">
                  Loading lead details
                </h5>

                <p className="text-muted mb-0">
                  Please wait...
                </p>
              </CardBody>
            </Card>
          </Container>
        </div>

        <ToastContainer closeButton={false} />
      </React.Fragment>
    );
  }

  if (!lead) {
    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <BreadCrumb
              title="Lead Details"
              pageTitle="Leads"
            />

            <Card>
              <CardBody className="text-center py-5">
                <div className="avatar-lg mx-auto mb-4">
                  <div className="avatar-title bg-danger-subtle text-danger rounded-circle fs-24">
                    !
                  </div>
                </div>

                <h5>Lead not found</h5>

                <p className="text-muted mb-4">
                  The requested lead could not be found.
                </p>

                <Button
                  color="primary"
                  onClick={() => navigate("/leads")}
                >
                  <i className="ri-arrow-left-line me-1" />
                  Back to All Leads
                </Button>
              </CardBody>
            </Card>
          </Container>
        </div>

        <ToastContainer closeButton={false} />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Lead Details"
            pageTitle="Leads"
          />

          {/* Back + Actions */}
          <Row className="mb-3">
            <Col>
              <div className="d-flex align-items-center justify-content-between">
                <Button
                  color="link"
                  className="text-muted p-0"
                  onClick={() => navigate("/leads")}
                >
                  <i className="ri-arrow-left-line me-1" />
                  Back to All Leads
                </Button>

                <div className="d-flex gap-2">
                  <Button
                    color="light"
                    onClick={() =>
                      navigate("/leads", {
                        state: {
                          editLeadId: lead._id,
                        },
                      })
                    }
                  >
                    <i className="ri-pencil-line me-1" />
                    Edit Lead
                  </Button>

                  <UncontrolledDropdown>
                    <DropdownToggle
                      color="light"
                      className="btn-icon"
                    >
                      <i className="ri-more-2-fill" />
                    </DropdownToggle>

                    <DropdownMenu end>
                      <DropdownItem
                        onClick={() =>
                          navigate("/leads", {
                            state: {
                              editLeadId: lead._id,
                            },
                          })
                        }
                      >
                        <i className="ri-pencil-line me-2" />
                        Edit Lead
                      </DropdownItem>

                      <DropdownItem divider />

                      <DropdownItem
                        className="text-danger"
                        onClick={() =>
                          setDeleteModal(true)
                        }
                      >
                        <i className="ri-delete-bin-line me-2" />
                        Delete Lead
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
              </div>
            </Col>
          </Row>

          <Row>
            {/* LEFT COLUMN */}
            <Col xl={8}>
              {/* Lead Header */}
              <Card>
                <CardBody>
                  <div className="d-flex align-items-start">
                    <div className="avatar-lg me-3">
                      <div className="avatar-title rounded-circle bg-primary-subtle text-primary fs-24">
                        {getInitials(leadName)}
                      </div>
                    </div>

                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        <h4 className="mb-1">
                          {leadName}
                        </h4>

                        {lead.leadNumber && (
                          <span className="text-muted">
                            #{lead.leadNumber}
                          </span>
                        )}
                      </div>

                      <p className="text-muted mb-3">
                        {lead.email ||
                          lead.mobile ||
                          "Education Lead"}
                      </p>

                      <div className="d-flex flex-wrap gap-2">
                        <Badge
                          className={`badge-label ${getStatusClass(
                            lead.status
                          )}`}
                        >
                          {lead.status || "New"}
                        </Badge>

                        <Badge
                          className={`badge-label ${getPriorityClass(
                            lead.priority
                          )}`}
                        >
                          {lead.priority || "Medium"}{" "}
                          Priority
                        </Badge>

                        {lead.source && (
                          <Badge
                            className="badge-label bg-light text-muted"
                          >
                            {lead.source}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="border-top mt-4 pt-4">
                    <div className="d-flex flex-wrap gap-2">
                      {phone && (
                        <Button
                          color="light"
                          tag="a"
                          href={`tel:${phone}`}
                        >
                          <i className="ri-phone-line text-success me-1" />
                          Call
                        </Button>
                      )}

                      {email && (
                        <Button
                          color="light"
                          tag="a"
                          href={`mailto:${email}`}
                        >
                          <i className="ri-mail-line text-primary me-1" />
                          Email
                        </Button>
                      )}

                      {whatsapp && (
                        <Button
                          color="light"
                          tag="a"
                          target="_blank"
                          rel="noreferrer"
                          href={`https://wa.me/${String(
                            whatsapp
                          ).replace(/\D/g, "")}`}
                        >
                          <i className="ri-whatsapp-line text-success me-1" />
                          WhatsApp
                        </Button>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <SectionTitle>
                    Contact Information
                  </SectionTitle>
                </CardHeader>

                <CardBody>
                  <Row>
                    <Col md={6}>
                      <DetailItem
                        label="Full Name"
                        value={leadName}
                      />

                      <DetailItem
                        label="Primary Phone"
                      >
                        {phone ? (
                          <a
                            href={`tel:${phone}`}
                            className="text-body"
                          >
                            {phone}
                          </a>
                        ) : (
                          "—"
                        )}
                      </DetailItem>

                      <DetailItem
                        label="Alternate Phone"
                        value={
                          lead.alternatePhone ||
                          lead.alternateMobile
                        }
                      />
                    </Col>

                    <Col md={6}>
                      <DetailItem label="Email">
                        {email ? (
                          <a
                            href={`mailto:${email}`}
                            className="text-body"
                          >
                            {email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </DetailItem>

                      <DetailItem
                        label="WhatsApp"
                        value={lead.whatsapp}
                      />

                      <DetailItem
                        label="Created On"
                        value={formatDate(
                          lead.createdAt,
                          true
                        )}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Education Profile */}
              <Card>
                <CardHeader>
                  <SectionTitle>
                    Education Profile
                  </SectionTitle>
                </CardHeader>

                <CardBody>
                  {profile ? (
                    <Row>
                      <Col md={6}>
                        <DetailItem
                          label="Student Name"
                          value={
                            profile.studentName
                          }
                        />

                        <DetailItem
                          label="Class Applying For"
                          value={
                            profile.classApplyingFor ||
                            profile.className
                          }
                        />

                        <DetailItem
                          label="Course"
                          value={profile.course}
                        />

                        <DetailItem
                          label="Program"
                          value={profile.program}
                        />
                      </Col>

                      <Col md={6}>
                        <DetailItem
                          label="Academic Year"
                          value={
                            profile.academicYear
                          }
                        />

                        <DetailItem
                          label="Previous School"
                          value={
                            profile.previousSchool
                          }
                        />

                        <DetailItem
                          label="Percentage / Marks"
                          value={
                            profile.percentage ||
                            profile.marks
                          }
                        />

                        <DetailItem
                          label="Specialization"
                          value={
                            profile.specialization
                          }
                        />
                      </Col>
                    </Row>
                  ) : (
                    <div className="text-center py-4">
                      <i className="ri-graduation-cap-line fs-32 text-muted" />

                      <p className="text-muted mb-0 mt-2">
                        No education profile has been
                        added for this lead.
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Lead Information */}
              <Card>
                <CardHeader>
                  <SectionTitle>
                    Lead Information
                  </SectionTitle>
                </CardHeader>

                <CardBody>
                  <Row>
                    <Col md={6}>
                      <DetailItem
                        label="Lead Number"
                        value={lead.leadNumber}
                      />

                      <DetailItem
                        label="Source"
                        value={lead.source}
                      />

                      <DetailItem
                        label="Source Platform"
                        value={
                          lead.sourcePlatform
                        }
                      />

                      <DetailItem
                        label="Campaign"
                        value={
                          lead.campaignName ||
                          lead.campaign?.name
                        }
                      />
                    </Col>

                    <Col md={6}>
                      <DetailItem
                        label="Status"
                      >
                        <Badge
                          className={`badge-label ${getStatusClass(
                            lead.status
                          )}`}
                        >
                          {lead.status || "New"}
                        </Badge>
                      </DetailItem>

                      <DetailItem
                        label="Priority"
                      >
                        <Badge
                          className={`badge-label ${getPriorityClass(
                            lead.priority
                          )}`}
                        >
                          {lead.priority ||
                            "Medium"}
                        </Badge>
                      </DetailItem>

                      <DetailItem
                        label="Assigned To"
                        value={
                          lead.assignedTo?.name ||
                          lead.assignedToName
                        }
                      />

                      <DetailItem
                        label="Next Follow-up"
                        value={formatDate(
                          lead.nextFollowUpAt ||
                            lead.nextFollowUp
                        )}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <SectionTitle>
                    Notes & Requirements
                  </SectionTitle>
                </CardHeader>

                <CardBody>
                  {lead.notes ||
                  lead.requirement ||
                  lead.remarks ? (
                    <div className="bg-light rounded p-3">
                      <p className="text-muted mb-0">
                        {lead.notes ||
                          lead.requirement ||
                          lead.remarks}
                      </p>
                    </div>
                  ) : (
                    <div className="text-muted">
                      No notes or requirements have been
                      added.
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* RIGHT COLUMN */}
            <Col xl={4}>
              {/* Summary */}
              <Card>
                <CardHeader>
                  <SectionTitle>
                    Lead Summary
                  </SectionTitle>
                </CardHeader>

                <CardBody>
                  <div className="d-flex align-items-center mb-4">
                    <div className="avatar-sm me-3">
                      <div className="avatar-title rounded-circle bg-primary-subtle text-primary">
                        {getInitials(leadName)}
                      </div>
                    </div>

                    <div>
                      <h6 className="mb-1">
                        {leadName}
                      </h6>

                      <span className="text-muted fs-12">
                        {lead.leadNumber ||
                          "Lead"}
                      </span>
                    </div>
                  </div>

                  <div className="border-top pt-3">
                    <DetailItem
                      label="Lead Status"
                    >
                      <Badge
                        className={`badge-label ${getStatusClass(
                          lead.status
                        )}`}
                      >
                        {lead.status || "New"}
                      </Badge>
                    </DetailItem>

                    <DetailItem
                      label="Priority"
                    >
                      <Badge
                        className={`badge-label ${getPriorityClass(
                          lead.priority
                        )}`}
                      >
                        {lead.priority ||
                          "Medium"}
                      </Badge>
                    </DetailItem>

                    <DetailItem
                      label="Lead Source"
                      value={lead.source}
                    />

                    <DetailItem
                      label="Created"
                      value={formatDate(
                        lead.createdAt
                      )}
                    />

                    <DetailItem
                      label="Last Updated"
                      value={formatDate(
                        lead.updatedAt
                      )}
                    />
                  </div>
                </CardBody>
              </Card>

              {/* Follow-up */}
              <Card>
                <CardHeader>
                  <SectionTitle>
                    Follow-up
                  </SectionTitle>
                </CardHeader>

                <CardBody>
                  <DetailItem
                    label="Next Follow-up"
                    value={formatDate(
                      lead.nextFollowUpAt ||
                        lead.nextFollowUp,
                      true
                    )}
                  />

                  <DetailItem
                    label="Follow-up Status"
                    value={
                      lead.followUpStatus ||
                      (lead.nextFollowUpAt
                        ? "Scheduled"
                        : "Not Scheduled")
                    }
                  />

                  <Button
                    color="primary"
                    outline
                    className="w-100"
                    onClick={() =>
                      navigate(
                        `/leads/${lead._id}/follow-up`
                      )
                    }
                  >
                    <i className="ri-calendar-line me-1" />
                    Manage Follow-up
                  </Button>
                </CardBody>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between">
                  <SectionTitle>
                    Recent Activity
                  </SectionTitle>

                  {activities.length > 0 && (
                    <span className="text-muted fs-12">
                      {activities.length}{" "}
                      activities
                    </span>
                  )}
                </CardHeader>

                <CardBody>
                  {activities.length > 0 ? (
                    <div className="activity-timeline">
                      {activities
                        .slice(0, 8)
                        .map((activity, index) => (
                          <div
                            className="d-flex mb-4"
                            key={
                              activity._id ||
                              index
                            }
                          >
                            <div className="avatar-xs me-3 flex-shrink-0">
                              <div className="avatar-title rounded-circle bg-light text-primary">
                                <i className="ri-time-line" />
                              </div>
                            </div>

                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between gap-2">
                                <h6 className="fs-13 mb-1">
                                  {activity.type ||
                                    activity.activityType ||
                                    "Activity"}
                                </h6>

                                <span className="text-muted fs-11">
                                  {formatDate(
                                    activity.createdAt ||
                                      activity.date
                                  )}
                                </span>
                              </div>

                              <p className="text-muted fs-12 mb-0">
                                {activity.description ||
                                  activity.notes ||
                                  activity.message ||
                                  "Activity recorded."}
                              </p>

                              {activity.userId?.name && (
                                <small className="text-muted">
                                  By{" "}
                                  {
                                    activity
                                      .userId
                                      .name
                                  }
                                </small>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <i className="ri-history-line fs-32 text-muted" />

                      <p className="text-muted mb-0 mt-2">
                        No activities recorded yet.
                      </p>
                    </div>
                  )}
                </CardBody>

                {activities.length > 8 && (
                  <div className="card-footer text-center">
                    <Button
                      color="link"
                      className="text-primary"
                      onClick={() =>
                        navigate(
                          `/leads/${lead._id}/activities`
                        )
                      }
                    >
                      View All Activities
                      <i className="ri-arrow-right-line ms-1" />
                    </Button>
                  </div>
                )}
              </Card>

              {/* Attribution */}
              <Card>
                <CardHeader>
                  <SectionTitle>
                    Attribution
                  </SectionTitle>
                </CardHeader>

                <CardBody>
                  <DetailItem
                    label="Source"
                    value={lead.source}
                  />

                  <DetailItem
                    label="Source Platform"
                    value={
                      lead.sourcePlatform
                    }
                  />

                  <DetailItem
                    label="Campaign"
                    value={
                      lead.campaignName ||
                      lead.campaign?.name
                    }
                  />

                  <DetailItem
                    label="Created Via"
                    value={
                      lead.leadCreatedVia
                    }
                  />

                  <DetailItem
                    label="Landing Page"
                    value={
                      lead.landingPage ||
                      lead.utmLandingPage
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        isDeleting={deleting}
      />

      <ToastContainer closeButton={false} />
    </React.Fragment>
  );
};

export default LeadDetails;